import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useToast } from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import handleToast from '../components/HandleToast'
import { getApiErrorMessage } from '../api/api'

interface UseFormMutationOptions<TData, TVariables> {
  mutationFn: (variables: TVariables) => Promise<TData>
  queryKey: string | string[]
  successMessage?: string
  onClose?: () => void
  onSuccess?: (data: TData) => void
}

export function useFormMutation<TData = unknown, TVariables = unknown>({
  mutationFn,
  queryKey,
  successMessage,
  onClose,
  onSuccess,
}: UseFormMutationOptions<TData, TVariables>) {
  const queryClient = useQueryClient()
  const toast = useToast()
  const { t } = useTranslation()

  return useMutation({
    mutationFn,
    onSuccess: (data) => {
      handleToast(
        toast,
        true,
        t('common.states.success'),
        successMessage ?? t('messages.updateSuccess')
      )
      queryClient.invalidateQueries({
        queryKey: Array.isArray(queryKey) ? queryKey : [queryKey],
      })
      onSuccess?.(data)
      onClose?.()
    },
    onError: (error: Error) => {
      const errorMessage = getApiErrorMessage(error)
      handleToast(
        toast,
        false,
        t('common.states.error'),
        errorMessage
      )
    },
  })
}
